import { sb } from './db';
import { DEFAULT_MARKETPLACE_SERVICES } from './constants';

export const ORG_JSON_KEYS = {
  customReports: 'custom_reports',
  customFields: 'custom_fields',
  marketplaceCatalogs: 'marketplace_catalogs',
  marketplaceServicesLegacy: 'marketplace_services',
  freeResources: 'free_resources',
  savedDashboards: 'saved_dashboards',
  savedReportViews: 'saved_report_views',
};

const DEFAULT_CUSTOM_FIELDS = { clients: [], tasks: [], employees: [] };

export const DEFAULT_FREE_RESOURCES = [
  {
    id: 'g1', name: 'Startup Kit 2025',
    desc: 'Complete startup guide — legal checklist, tax calendar, compliance tracker',
    icon: 'gift', format: 'PDF', pages: 24, triggerOn: 'registration_complete',
    downloadUrl: '', color: 'var(--gold)', bundleIds: [],
  },
  {
    id: 'g2', name: 'GST Compliance Calendar',
    desc: 'Month-by-month GST filing calendar with due dates and penalties',
    icon: 'calendar', format: 'PDF', pages: 8, triggerOn: 'bundle_purchase',
    downloadUrl: '', color: 'var(--blue)', bundleIds: [],
  },
  {
    id: 'g3', name: 'Director KYC Checklist',
    desc: 'Complete KYC document checklist for all directors',
    icon: 'clipboard', format: 'PDF', pages: 4, triggerOn: 'always',
    downloadUrl: '', color: 'var(--green)', bundleIds: [],
  },
  {
    id: 'g4', name: 'Startup India Guide',
    desc: 'Step-by-step guide to Startup India recognition and benefits',
    icon: 'rocket', format: 'PDF', pages: 16, triggerOn: 'registration_complete',
    downloadUrl: '', color: '#7C3AED', bundleIds: [],
  },
];

export function normalizeMarketplaceCatalogs(catalogs, legacyServices) {
  if (Array.isArray(catalogs) && catalogs.length && Array.isArray(catalogs[0]?.services)) {
    return catalogs;
  }
  let services = legacyServices;
  if (typeof legacyServices === 'string') {
    try { services = JSON.parse(legacyServices); } catch { services = null; }
  }
  if (!Array.isArray(services) || !services.length) services = DEFAULT_MARKETPLACE_SERVICES;
  return [{
    id: 'cat_default',
    name: 'Main Catalog',
    desc: 'Default service marketplace',
    isDefault: true,
    services,
  }];
}

export function parseOrgJsonRows(rows = []) {
  const byKey = {};
  for (const row of rows) {
    if (!row?.key) continue;
    try {
      byKey[row.key] = JSON.parse(row.value || 'null');
    } catch {
      byKey[row.key] = null;
    }
  }
  const catalogs = normalizeMarketplaceCatalogs(
    byKey[ORG_JSON_KEYS.marketplaceCatalogs],
    byKey[ORG_JSON_KEYS.marketplaceServicesLegacy],
  );
  const rawCustom = byKey[ORG_JSON_KEYS.customFields];
  const customFields = rawCustom && typeof rawCustom === 'object' && !Array.isArray(rawCustom)
    ? {
        clients: Array.isArray(rawCustom.clients) ? rawCustom.clients : [],
        tasks: Array.isArray(rawCustom.tasks) ? rawCustom.tasks : [],
        employees: Array.isArray(rawCustom.employees) ? rawCustom.employees : [],
      }
    : DEFAULT_CUSTOM_FIELDS;

  return {
    customReports: Array.isArray(byKey[ORG_JSON_KEYS.customReports]) ? byKey[ORG_JSON_KEYS.customReports] : [],
    customFields,
    marketplaceCatalogs: catalogs,
    marketplaceServices: catalogs.find(c => c.isDefault)?.services || catalogs[0]?.services || DEFAULT_MARKETPLACE_SERVICES,
    freeResources: Array.isArray(byKey[ORG_JSON_KEYS.freeResources]) && byKey[ORG_JSON_KEYS.freeResources].length
      ? byKey[ORG_JSON_KEYS.freeResources]
      : DEFAULT_FREE_RESOURCES,
    savedDashboards: Array.isArray(byKey[ORG_JSON_KEYS.savedDashboards]) ? byKey[ORG_JSON_KEYS.savedDashboards] : [],
    savedReportViews: Array.isArray(byKey[ORG_JSON_KEYS.savedReportViews]) ? byKey[ORG_JSON_KEYS.savedReportViews] : [],
  };
}

export async function loadOrgJsonSettings() {
  const rows = await sb.select('org_settings');
  return parseOrgJsonRows(Array.isArray(rows) ? rows : []);
}

export async function persistOrgJsonSetting(key, value, setOrg, orgField) {
  const json = JSON.stringify(value);
  try {
    await sb.update('org_settings', 'key', key, { value: json });
  } catch {
    await sb.insert('org_settings', { key, value: json });
  }
  if (setOrg && orgField) {
    setOrg(prev => ({ ...prev, [orgField]: value }));
  }
}

/** Client companies that appear to have purchased a bundle (line-item name overlap). */
export function clientPurchasedBundleIds(clientId, invoices, bundles) {
  const ids = new Set();
  const clientInvs = (invoices || []).filter(i => i.clientId === clientId && i.status !== 'void');
  for (const inv of clientInvs) {
    const iNames = (inv.lineItems || []).map(li => (li.name || '').toLowerCase()).filter(Boolean);
    for (const b of bundles || []) {
      const bNames = (b.lineItems || []).map(li => (li.name || '').toLowerCase()).filter(Boolean);
      if (!bNames.length) continue;
      const hit = bNames.filter(n => iNames.some(name => name.includes(n) || n.includes(name))).length;
      if (hit >= Math.min(2, bNames.length) || (bNames.length === 1 && hit === 1)) {
        ids.add(b.id);
      }
    }
  }
  return ids;
}

export function isFreeResourceUnlocked(gift, { regComplete, purchasedBundleIds, bundles }) {
  if (gift.triggerOn === 'always') return true;
  if (gift.triggerOn === 'registration_complete') return regComplete;
  const linked = gift.bundleIds || [];
  if (linked.some(id => purchasedBundleIds.has(id))) return true;
  for (const bid of purchasedBundleIds) {
    const b = (bundles || []).find(x => x.id === bid);
    if (b?.freeResourceIds?.includes(gift.id)) return true;
  }
  if (gift.triggerOn === 'bundle_purchase') return linked.length > 0 && linked.some(id => purchasedBundleIds.has(id));
  return false;
}
